import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import { useService } from 'modules/core/hooks';
import { DefaultTheme } from 'themes/interfaces/default-theme.interfaces';
import { BACKGROUND } from 'themes/utils/background-utils';
import {
  currentSelectedRoundSelector,
  userProfileSelector
} from 'modules/core/store/selectors';
import { Badge, Divider, Grid, Typography } from '@material-ui/core';
import { FeedCardPepco } from '../../layout/feed-card/feed-card-pepco';
import { UsersService } from 'modules/core/services';
import { defaultTheme } from 'themes/default-theme';
import { RankingService, BadgesService } from 'modules/user/services'
import { ActivityService } from 'modules/user/services/activity.services';
import { showApiErrors } from 'utils';
import { ApiError } from 'utils/api-error';
import { IMyIndividualRanking } from 'modules/user/interfaces/ranking.interface';
import { IBadgeItem } from 'modules/user/interfaces/badges.interface';
import { Heart } from 'modules/core/components/common/rich-editor/components/hearts';
import { SCORE_LIMIT_TO_PASS } from 'const/scoreTreshold';
import GwiazdaSvg from 'assets/pepco/Gwiazda.svg';
import OdkrywcaSvg from 'assets/pepco/Odkrywca.svg';
import RakietaSvg from 'assets/pepco/Rakieta.svg';
import AngelSvg from 'assets/pepco/Angel.svg';

const PEPCO_BADGES = [
  { name: 'Gwiazda', svg: GwiazdaSvg },
  { name: 'Odkrywca', svg: OdkrywcaSvg },
  { name: 'Rakieta', svg: RakietaSvg },
  { name: 'Angel', svg: AngelSvg },
];

const makeClasses = makeStyles<DefaultTheme>(theme => ({
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },

  personAvatar: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    marginRight: theme.spacing(1),
    overflow: 'hidden',
    '& img': {
      maxWidth: '100%'
    }
  },

  labelName: {
    ...defaultTheme.mixins.fonts.regular.s,
    color: '#171717',
  },

  labelScore: {
    ...defaultTheme.mixins.fonts.bold.s,
    color: '#171717',
  },

  labelWon: {
    ...defaultTheme.mixins.fonts.regular.xs,
  },

  divider: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
  },

  badgesGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  }
}));

export const DuelFeed = () => {
  const classes = makeClasses();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const currentRound = useSelector(currentSelectedRoundSelector);
  const [avatar, setAvatar] = useState<string>();

  const currentProfile = useSelector(userProfileSelector);
  const rankingService = useService(RankingService);
  const usersService = useService(UsersService);
  const activityService = useService(ActivityService);
  const badgesService = useService(BadgesService);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [totalMaxPoints, setTotalMaxPoints] = useState<number>(0);
  const [myRanking, setMyRanking] = useState<IMyIndividualRanking>();
  const [badges, setBadges] = useState<IBadgeItem[]>([]);

  const isHeartFull = totalMaxPoints > 0 && (totalPoints / totalMaxPoints) * 100 >= SCORE_LIMIT_TO_PASS;

  const getMyPoints = async () => {
    try {
      if (currentRound?.id) {
        const response = await activityService.fetchActivities({ page: 1, size: 9999 }, currentRound.id, 'mine');
        if (response?.records) {
          const points = response.records.reduce((sum, a) => sum + (a.points || 0), 0);
          const maxPoints = response.records.reduce((sum, a) => sum + (a.maxPoints || 0), 0);
          setTotalPoints(points);
          setTotalMaxPoints(maxPoints);
        }
      }
    } catch (e) {
      showApiErrors(e as ApiError, enqueueSnackbar);
    }
  }
  const getMyRankinkPosition = async () => {
    try {
      const response = await rankingService.getIndividualRanking(2);
      if (response) {
        setMyRanking(response.all[0])
      }
    } catch (e) {
      showApiErrors(e as ApiError, enqueueSnackbar);
    }
  }

  const fetchBadges = async () => {
    try {
      const response = await badgesService.getUserBadges();
      if (Array.isArray(response)) {
        setBadges(response);
      }
    } catch (e) {
      showApiErrors(e as ApiError, enqueueSnackbar);
    }
  };

  useEffect(() => {
    fetchAvatar();
    getMyPoints();
    getMyRankinkPosition();
    fetchBadges();
  }, [currentRound]);

  const fetchAvatar = async () => {
    try {
      const avatarUrl = await usersService.fetchAvatar(currentProfile?.avatar);
      setAvatar(avatarUrl);
    } catch (e) {
      showApiErrors(e as ApiError, enqueueSnackbar);
    }
  };

  const safeBadges = Array.isArray(badges) ? badges : [];
  const pepcoBadgeData = PEPCO_BADGES.map(({ name, svg }) => ({
    name,
    svg,
    count: safeBadges.filter(b => b.name === name).reduce((sum, b) => sum + b.occurance, 0),
  }));

  return (
    <>
      <FeedCardPepco
        height={'500px'}
        headingColor={BACKGROUND.NONE}
        title={`${t('userPanel.dashboard.myScore')} - ${currentRound.name}`}
      >
        <div className={classes.avatarRow}>
          <div className={classes.personAvatar}>
            {avatar && <img src={avatar} alt='' />}
          </div>
          <div style={{
            filter: isHeartFull ? 'none' : 'grayscale(100%)',
            opacity: isHeartFull ? 1 : 0.5,
            transition: 'filter 0.3s ease, opacity 0.3s ease',
          }}>
            <Heart full={true} />
          </div>
        </div>
        <Typography className={classes.labelName}>{currentProfile?.name + ' ' + currentProfile?.surname}</Typography>
        {myRanking && <Typography className={classes.labelScore}>{t('userPanel.myRankingFeed.myPlace', { place: myRanking.placement ?? 0 })}</Typography>}
        <Divider className={classes.divider} />
        <Grid container>
          <Grid item xs={6}>
            <Typography className={classes.labelWon}>{t('userPanel.duels.points')}</Typography>
            <Typography className={classes.labelScore}>{totalPoints}/{totalMaxPoints}</Typography>
          </Grid>
          <Grid item xs={6}>
            <div className={classes.badgesGrid}>
              {pepcoBadgeData.map(({ name, svg, count }) => (
                <Badge key={name} badgeContent={count > 1 ? count : 0} color="error" showZero={false} overlap="circle">
                  <img
                    src={svg}
                    alt={name}
                    style={{
                      height: 52,
                      filter: count === 0 ? 'grayscale(100%)' : 'none',
                      opacity: count === 0 ? 0.3 : 1,
                    }}
                  />
                </Badge>
              ))}
            </div>
          </Grid>
        </Grid>
      </FeedCardPepco>
    </>
  );
};
